// src/pages/Batches.js
import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Form, Spinner } from "react-bootstrap";
import {
  createBatch,
  getBatches,
  updateBatch,
  deleteBatch,
} from "../services/batchService";
import Loader from "../components/Loader";
import { PencilIcon, TrashIcon } from "lucide-react";
import { toast } from "react-toastify";

export default function Batches() {
  const [batches, setBatches] = useState([]);
  const [show, setShow] = useState(false);
  const [newBatch, setNewBatch] = useState({ name: "", status: "Planned" });
  const [editingBatchId, setEditingBatchId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    setLoading(true);
    const data = await getBatches();
    setBatches(data);
    setLoading(false);
  };

  const handleAddBatch = async () => {
    if (!newBatch.name) return toast.error("Batch name is required");
    setBtnLoading(true);

    const batchId = await createBatch(newBatch);
    setBatches([...batches, { id: batchId, ...newBatch }]);

    resetModal();
    toast.success("Batch added successfully");
  };

  const handleUpdateBatch = async () => {
    if (!editingBatchId) return;
    setBtnLoading(true);

    await updateBatch(editingBatchId, newBatch);
    setBatches(
      batches.map((b) => (b.id === editingBatchId ? { ...b, ...newBatch } : b))
    );

    resetModal();
    toast.success("Batch updated successfully");
  };

  const handleDeleteBatch = async (id) => {
    if (!window.confirm("Are you sure you want to delete this batch?")) return;
    setLoading(true);
    await deleteBatch(id);
    setBatches(batches.filter((b) => b.id !== id));
    setLoading(false);
    toast.success("Batch deleted successfully");
  };

  const handleEditBatch = (id) => {
    const batch = batches.find((b) => b.id === id);
    if (batch) {
      setEditingBatchId(id);
      setNewBatch(batch);
      setShow(true);
    }
  };

  const resetModal = () => {
    setShow(false);
    setEditingBatchId(null);
    setNewBatch({ name: "", status: "Planned" });
    setBtnLoading(false);
  };

  // Filtering + Sorting
  const filteredBatches = batches
    .filter((b) =>
      Object.values(b).some((val) =>
        String(val).toLowerCase().includes(filter.toLowerCase())
      )
    )
    .sort((a, b) => {
      const aVal = String(a[sortKey]).toLowerCase();
      const bVal = String(b[sortKey]).toLowerCase();
      if (aVal < bVal) return sortAsc ? -1 : 1;
      if (aVal > bVal) return sortAsc ? 1 : -1;
      return 0;
    });

  const totalPages = Math.ceil(filteredBatches.length / pageSize);
  const paginatedBatches = filteredBatches.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Batches ({batches.length})</h2>
        <Button onClick={() => setShow(true)}>+ Add Batch</Button>
      </div>

      {loading && <Loader />}

      <div className="mb-3">
        <Form.Control
          type="text"
          placeholder="Search batches..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      <Table bordered hover responsive>
        <thead className="table-light">
          <tr>
            <th>#</th>
            <th>Batch Name</th>
            <th>Status</th>
            <th>Start Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedBatches.length === 0 && (
            <tr>
              <td colSpan="5" className="text-center">
                No batches found.
              </td>
            </tr>
          )}
          {paginatedBatches.map((b, idx) => (
            <tr key={b.id}>
              <td>{idx + 1}</td>
              <td>{b.name}</td>
              <td>{b.status}</td>
              <td>{b.startDate || "—"}</td>
              <td>
                <div className="flex space-x-2">
                  <Button variant="info" onClick={() => handleEditBatch(b.id)}>
                    <PencilIcon size={16} />
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => handleDeleteBatch(b.id)}
                  >
                    <TrashIcon size={16} />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="flex justify-between items-center">
        <div>
          Page {currentPage} of {totalPages}
        </div>
        <div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Prev
          </Button>{" "}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Add/Edit Batch Modal */}
      <Modal show={show} onHide={resetModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingBatchId ? "Edit Batch" : "Add Batch"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Batch Name</Form.Label>
            <Form.Control
              type="text"
              value={newBatch.name}
              onChange={(e) =>
                setNewBatch({ ...newBatch, name: e.target.value })
              }
              placeholder="Enter batch name"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={newBatch.status}
              onChange={(e) =>
                setNewBatch({ ...newBatch, status: e.target.value })
              }
            >
              <option>Planned</option>
              <option>Ongoing</option>
              <option>Completed</option>
              <option>Cancelled</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              value={newBatch.startDate || ""}
              onChange={(e) =>
                setNewBatch({ ...newBatch, startDate: e.target.value })
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={btnLoading}
            onClick={editingBatchId ? handleUpdateBatch : handleAddBatch}
          >
            {btnLoading ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  className="text-white"
                />
                <span className="ml-2">
                  {editingBatchId ? "Updating..." : "Saving..."}
                </span>
              </>
            ) : (
              <>{editingBatchId ? "Update Batch" : "Add Batch"}</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
